import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = "/api";

/**
 * Abreise-Seite für Gäste (öffentlich, ohne Login)
 * 
 * MAXIMALE ISOLATION:
 * - Kein useParams (verursacht DOM-Fehler)
 * - Keine React-Router-Hooks
 * - Nur natives HTML
 * - Property-ID aus URL manuell extrahieren
 */
const DeparturePage = () => {
  // Property-ID aus URL manuell extrahieren (NICHT useParams!)
  const getPropertyIdFromUrl = () => {
    const path = window.location.pathname;
    const match = path.match(/\/departure\/([^/]+)/);
    return match ? match[1] : null;
  };
  
  const [propertyId] = useState(getPropertyIdFromUrl);
  const [pageState, setPageState] = useState('loading');
  const [propertyData, setPropertyData] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [departureTime, setDepartureTime] = useState('10:00');

  useEffect(() => {
    // Heutiges Datum setzen
    const today = new Date().toISOString().split('T')[0];
    setDepartureDate(today);
    
    // Property laden
    if (!propertyId) {
      setErrorMessage('Ungültiger Link. Bitte scannen Sie den QR-Code erneut.');
      setPageState('error');
      return;
    }
    
    const loadProperty = async () => {
      try {
        const response = await axios.get(`${API}/departure/property/${propertyId}`);
        setPropertyData(response.data);
        setPageState('form');
      } catch (err) {
        setErrorMessage('Das Objekt konnte nicht gefunden werden.');
        setPageState('error');
      }
    };
    
    loadProperty();
  }, [propertyId]);

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageState('submitting');
    
    try {
      const response = await axios.post(`${API}/departure/report`, {
        property_id: propertyId,
        departure_date: departureDate,
        departure_time: departureTime
      });
      
      // Erfolg
      setResult(response.data);
      setPageState('success');
      
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Ein Fehler ist aufgetreten.');
      setPageState('form');
    }
  };

  // Inline Styles
  const styles = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #f0f9ff, #ffffff)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: 'Arial, sans-serif'
    },
    card: {
      width: '100%',
      maxWidth: '400px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    },
    cardHeader: {
      padding: '24px',
      paddingBottom: '8px',
      textAlign: 'center'
    },
    cardContent: {
      padding: '24px',
      paddingTop: '0'
    },
    logo: {
      height: '64px',
      width: 'auto',
      marginBottom: '16px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#0E4C92',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#64748b',
      margin: '0'
    },
    infoBox: {
      backgroundColor: '#eff6ff',
      border: '1px solid #dbeafe',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '24px'
    },
    infoText: {
      fontSize: '14px',
      color: '#334155',
      margin: '0',
      lineHeight: '1.5'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: '#475569',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      padding: '0 12px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    button: {
      width: '100%',
      height: '48px',
      backgroundColor: '#0E4C92',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonDisabled: {
      backgroundColor: '#94a3b8',
      cursor: 'not-allowed'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#d1fae5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px auto',
      fontSize: '40px'
    },
    successTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
      textAlign: 'center'
    },
    successText: {
      fontSize: '16px',
      color: '#475569',
      textAlign: 'center',
      lineHeight: '1.6',
      margin: '0 0 16px 0'
    },
    successHint: {
      fontSize: '14px',
      color: '#94a3b8',
      textAlign: 'center',
      marginTop: '32px'
    },
    detailsBox: {
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      textAlign: 'center'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px'
    },
    errorText: {
      fontSize: '14px',
      color: '#b91c1c',
      margin: '0'
    },
    loadingSpinner: {
      width: '32px',
      height: '32px',
      border: '3px solid #e2e8f0',
      borderTopColor: '#0E4C92',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 16px auto'
    }
  };

  // LOADING
  if (pageState === 'loading') {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: '#64748b' }}>Lade...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ERROR
  if (pageState === 'error' && !propertyData) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ 
              width: '64px', height: '64px', borderRadius: '50%', 
              backgroundColor: '#fee2e2', margin: '0 auto 16px auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '32px'
            }}>
              ⚠️
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>Fehler</h2>
            <p style={{ color: '#64748b' }}>{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS
  if (pageState === 'success') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={styles.successIcon}>✓</div>
            
            <h2 style={styles.successTitle}>Vielen Dank für Ihre Nachricht.</h2>
            
            {result?.property_name && (
              <div style={styles.detailsBox}>
                <p style={{ fontWeight: '500', color: '#334155', margin: '0 0 4px 0' }}>
                  {result.property_name}
                </p>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0' }}>
                  Abreise: {result.departure_date} um {result.departure_time} Uhr
                </p>
              </div>
            )}
            
            <p style={styles.successText}>
              Die Information wurde an unser Reinigungsteam weitergeleitet und hilft uns sehr.
            </p>
            <p style={styles.successText}>
              Wir wünschen Ihnen schon jetzt eine gute Heimreise.
            </p>
            
            <p style={styles.successHint}>
              Sie können diese Seite jetzt schließen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // FORM
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <img 
            src="https://customer-assets.emergentagent.com/job_maintenance-hub-128/artifacts/ozn5f15c_LOGOneu.jpg" 
            alt="GB Sylt" 
            style={styles.logo}
          />
          <h1 style={styles.title}>
            {propertyData?.texts?.form_title || 'Abreise mitteilen'}
          </h1>
          <p style={styles.subtitle}>
            {propertyData?.property_name}
          </p>
        </div>
        
        <div style={styles.cardContent}>
          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              {propertyData?.texts?.form_description || 
                'Die reguläre Abreise ist um 10:00 Uhr. Wenn Sie früher abreisen, freut sich unser Reinigungsteam über diese Information.'}
            </p>
          </div>

          {errorMessage && pageState === 'form' && (
            <div style={styles.errorBox}>
              <p style={styles.errorText}>{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                📅 Abreisedatum
              </label>
              <input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                required
                disabled={pageState === 'submitting'}
                style={styles.input}
                data-testid="departure-date-input"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>
                🕐 Voraussichtliche Abreisezeit
              </label>
              <input
                type="time"
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
                required
                disabled={pageState === 'submitting'}
                style={styles.input}
                data-testid="departure-time-input"
              />
            </div>

            <button
              type="submit"
              disabled={pageState === 'submitting'}
              style={{
                ...styles.button,
                ...(pageState === 'submitting' ? styles.buttonDisabled : {})
              }}
              data-testid="submit-departure-btn"
            >
              {pageState === 'submitting' ? (
                '⏳ Wird gesendet...'
              ) : (
                '✉️ Abreise absenden'
              )}
            </button>
          </form>
          
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    </div>
  );
};

export default DeparturePage;
